import React from 'react';
import { string, SchemaOf, object } from 'yup';
import { useStore } from '../../../../store/store-provider';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { IRouteParams } from '../../../../pages/container/container';
import { localStoragePageInfo } from '../../../../utils/services/local-storage-service';
import { EditableState, TQuestionEntity } from '../../../../store/question-store/question-entetiy';
import {
  EMAIL_VALIDATION_ERROR_MESSAGE,
  MEDIUM_PLAIN_TEXT_LENGTH,
  MEDIUM_PLAIN_TEXT_VALIDATION_MESSAGE,
  REQUIRED_ERROR_MESSAGE,
  SHORT_PLAIN_TEXT_LENGTH,
  SHORT_PLAIN_TEXT_VALIDATION_MESSAGE
} from '../../../../constans/common-constants';
import { FormikWrapper, IFormField } from '../../../shared/formik-wrapper/formik-wrapper';
import TextArea from '../../../shared/textarea/textarea';
import './question-action-form.scss';
import { getMixpanelService } from '../../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../../constans/mixpanel-contants';
import { SenderStatus } from '../../../../graphql/API';

export interface IQuestionActionDataForm {
  senderName: string;
  senderEmail: string;
  topic: string;
  message: string;
}

interface IProps {
  question?: TQuestionEntity;
  setEditableQuestionState: (id: string | null, state: EditableState) => void;
  editableQuestionId?: string | null;
}


interface QuestionInputSchema {
  senderName: string;
  message: string;
  topic?: string;
  senderEmail: string;
}

const inputsSchema: SchemaOf<QuestionInputSchema> = object().shape({
  senderName: string()
    .trim()
    .max(SHORT_PLAIN_TEXT_LENGTH, SHORT_PLAIN_TEXT_VALIDATION_MESSAGE)
    .required(`Name ${REQUIRED_ERROR_MESSAGE}`),
  message: string()
    .trim()
    .max(MEDIUM_PLAIN_TEXT_LENGTH, MEDIUM_PLAIN_TEXT_VALIDATION_MESSAGE)
    .required(`Question ${REQUIRED_ERROR_MESSAGE}`),
  topic: string()
    .trim()
    .max(SHORT_PLAIN_TEXT_LENGTH, SHORT_PLAIN_TEXT_VALIDATION_MESSAGE),
  senderEmail: string()
    .trim()
    .max(SHORT_PLAIN_TEXT_LENGTH, SHORT_PLAIN_TEXT_VALIDATION_MESSAGE)
    .email(EMAIL_VALIDATION_ERROR_MESSAGE)
    .required(`Email ${REQUIRED_ERROR_MESSAGE}`)
});

const QuestionActionForm = observer(({ question, setEditableQuestionState }: IProps) => {
  const { questionStore, podiumInfoStore } = useStore();
  const { setShowCreateQuestion, handleSubmitQuestionForm } = questionStore;
  const { presentationInfo } = podiumInfoStore;
  const { id } = useParams<IRouteParams>();
  const { userName, userEmail } = localStoragePageInfo.getStorageInfo(id);

  const isSubmittedQuestion = question?.senderStatus === SenderStatus.AI;

  const FORM_FIELDS: IFormField[] = [
    { fieldName: 'senderName', value: 'Name', type: 'text', disabled: isSubmittedQuestion },
    { fieldName: 'senderEmail', value: 'Email', type: 'text', disabled: isSubmittedQuestion },
    ...(isSubmittedQuestion ? [{ fieldName: 'topic', value: 'Topic', type: 'text' }] : []),
    { fieldName: 'message', value: 'Question', component: TextArea, disabled: isSubmittedQuestion },
  ];

  const handleSubmit = (data:IQuestionActionDataForm) => {
    handleSubmitQuestionForm(id, question, data, presentationInfo.presentationName);

    let formType;

    if (question) {
      formType = MIXPANEL_EVENT_TYPES.SAVED_EDIT;
    } else {
      formType = MIXPANEL_EVENT_TYPES.SAVED_CREATE;
    }

    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, formType);
    if (isSubmittedQuestion) {
      getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QA_AI_AUTO_ANALYSIS_TOPIC_EDITED, null);
    }
  };

  const handleCancelClick = () => question?.getEditableState() === EditableState.OPENED ?
    setEditableQuestionState(question?.messageId || null, EditableState.CLOSED)
    : setShowCreateQuestion(false);

  const initialValues: IQuestionActionDataForm = {
    senderName: question?.senderName || userName || '',
    senderEmail: question?.senderEmail || userEmail || '',
    topic: question?.topic || '',
    message: question?.message || ''
  };

  return (
    <div>
      <FormikWrapper
        rootClassname="question-action-form"
        enableReinitialize={false}
        enableLabelValue
        enableDefaultSubmitButton
        cancelClick={handleCancelClick}
        handleSubmit={handleSubmit}
        initialValues={initialValues}
        inputsSchema={inputsSchema}
        inputFields={FORM_FIELDS}
      />
    </div>
  );
});

export default QuestionActionForm;
